/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import _ from "lodash";
// react-router components
import { Routes, Route, useLocation, Navigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import logo from "assets/images/arjion_b.png";

// Material Dashboard 2 PRO React TS components
import MDBox from "./components/MDBox";

// Material Dashboard 2 PRO React TS exampless
import Sidenav from "./examples/Sidenav";
import Configurator from "./examples/Configurator";

// Material Dashboard 2 PRO React TS themes
import themeRTL from "./assets/theme/theme-rtl";
import { useSelector } from "react-redux";

// Material Dashboard 2 PRO React TS Dark Mode themes
import themeDark from "./assets/theme-dark";
import themeDarkRTL from "./assets/theme-dark/theme-rtl";

// Material Dashboard 2 PRO React TS routes
import { StoreType } from "./types/genericTypes";
import routes from "./routes";
import textosMx from "./idiomas/mx";
import textoEn from "./idiomas/en";

// Material Dashboard 2 PRO React TS contexts
import { setOpenConfigurator, setMiniSidenav, useMaterialUIController } from "context";
import { IntlProvider, ReactIntlErrorCode } from "react-intl";

// Images
import brandWhite from "./assets/images/logo-ct.png";
import brandDark from "./assets/images/logo-ct-dark.png";
import NotFoundPage from "./pages/NotFoundPage";
import { Avatar } from "@mui/material";
import MainPage from "./pages/MainPage";
import PublicRouter from "hocs/PublicRouter";
import Categorias from "pages/Categorias";
import TipoProducto from "pages/TipoProducto";
import UnidadMedida from "pages/UnidadMedida";
import Ubicacion from "pages/Ubicacion";
import Almacen from "pages/Almacen";
import Inventario from "pages/Inventario";
import AlmacenPage from "pages/Almacen";
import Requisicion from "pages/Requisicion";
import RequisicionIndividual from "pages/RequisicionIndividual";
import Proveedores from "pages/Proveedores";
import CotizacionProveedor from "pages/CotizacionProveedor";
import Cotizaciones from "pages/Cotizaciones";
import OrdenCompraProveedor from "pages/OrdenCompraProveedor";
import OrdenCompra from "pages/OrdenCompra";
import Kardex from "pages/Kardex";
import Empresas from "pages/Empresas";
import KardexTabla from "pages/KardexTabla";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();
  const userName = useSelector((state: StoreType) => state?.app?.user?.data?.usuario || false);
  const fotoUser: any = useSelector((state: StoreType) => state?.app?.user?.data?.foto || logo);
  const local = useSelector((state: StoreType) => state?.app?.idioma || "mx");
  const ruta = useSelector((state: StoreType) => state?.app?.ruta || null);
  const idPerfilUsuario = useSelector((state: StoreType) => state?.app?.user?.data?.id_perfil || 0);

  const tipoUsuario = Number(idPerfilUsuario) || 0;
  const puedeVer = (r: any): boolean =>
    !tipoUsuario || !Array.isArray(r?.allow) || r.allow.includes(tipoUsuario);
  const filtrarRutas = (lista: any[]): any[] =>
    lista.filter(puedeVer).map((r: any) =>
      Array.isArray(r?.collapse) ? { ...r, collapse: filtrarRutas(r.collapse) } : r
    );

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const loadLocaleData = (locale: string) => {
    if (locale === "en") {
      return textoEn;
    }
    return textosMx;
  };

  const onError = (e: any) => {
    if (e.code === ReactIntlErrorCode.MISSING_DATA) {
      return;
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const routes_ = {
    name: userName,
    icon: <Avatar className="AvatarUser" alt={userName || ""} src={fotoUser} />,
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return (
    <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
      <CssBaseline />
      <IntlProvider onError={onError} locale={local} messages={loadLocaleData(local)}>
        <>
          <Sidenav
            key={""}
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="Módulo de Compras"
            routes={filtrarRutas(routes).map((a: any, key: number) =>
              key === 0 ? { ...a, ...routes_ } : a
            )}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        </>
        <Routes>
          {/* Redirección de / a /inicio */}
          <Route path="/" element={<Navigate to="/inicio" replace />} />
          {/* COTIZACIÓN POR PROVEEDOR INDIVIDUAL*/}
          <Route path="/detalle-cotizacion" element={<CotizacionProveedor />} />
          {/* ORDEN COMPRA POR PROVEEDOR INDIVIDUAL*/}
          <Route path="/detalle-oc" element={<OrdenCompraProveedor />} />
          {/* inicia rutas*/}
          <Route element={<PublicRouter />}>
            <Route path="/inicio" element={<MainPage />} />
            {/* --- REQUISICIÓN --- */}
            {/* AÑADIR NUEVA */}
            <Route path="/requisicion" element={<Requisicion />} />
            {/* VISTA INDIVIDUAL DE REQUISICIÓN */}
            <Route path="/requisicionInd" element={<RequisicionIndividual />} />
            {/* --- INVENTARIOS --- */}
            {/* CATEGORIAS */}
            <Route path="/categorias" element={<Categorias />} />
            {/* TIPOS DE PRODUCTO */}
            <Route path="/tipos-producto" element={<TipoProducto />} />
            {/* UNIDADES DE MEDIDA */}
            <Route path="/unidades-medida" element={<UnidadMedida />} />
            {/* UBICACIONES */}
            <Route path="/ubicaciones" element={<Ubicacion />} />
            {/* ALMACENES */}
            <Route path="/almacenes" element={<AlmacenPage />} />
            {/* CATÁLOGO DE PRODUCTOS */}
            <Route path="/catalogo-productos" element={<Inventario />} />
            {/* PROVEEDORES */}
            <Route path="/proveedores" element={<Proveedores />} />
            {/* COTIZACIONES */}
            <Route path="/cotizaciones" element={<Cotizaciones />} />
            {/* ORDENES DE COMPRA */}
            <Route path="/orden-compra" element={<OrdenCompra />} />
            {/* KARDEX */}
            <Route path="/kardex" element={<KardexTabla />} />
            {/* EMPRESAS */}
            <Route path="/empresas" element={<Empresas />} />
          </Route>

          {/* inicia pagina no encontrada */}
          <Route path="/404" element={<NotFoundPage />} />
          {/* fin pagina no encontrada */}
        </Routes>
      </IntlProvider>
    </ThemeProvider>
  );
}
